<template>
  <div class="header">
    <div
      v-if="['Datasets', 'PDFs', 'News'].includes($route.name)"
      class="header--full"
    >
      <div class="header--top">
        <div class="header--left">
          <div
            class="header__icon"
            v-html="icon"
          />

          <div
            v-if="paginator.total !== 0"
            class="header__info"
          >
            <p v-if="!['News'].includes($route.name)" class="header__info--xs">(<span>{{ separateThousands(paginator.from) }}</span>-<span>{{ separateThousands(paginator.to) }}</span> / <span>{{ separateThousands(paginator.total) }}</span>)</p>
            <p v-if="!['News'].includes($route.name)" class="header__info--sm"><span class="header__info--title">{{ title }}</span> (<span>{{ separateThousands(paginator.from) }}</span> to <span>{{ separateThousands(paginator.to) }}</span> from <span>{{ separateThousands(paginator.total) }}</span>)</p>
            <p v-if="!['News'].includes($route.name)" class="header__info--md"><span class="header__info--title">{{ title }}</span> (<span>{{ separateThousands(paginator.from) }}</span> to <span>{{ separateThousands(paginator.to) }}</span> from <span>{{ separateThousands(paginator.total) }}</span> result{{ paginator.total !== 1 ? 's' : '' }})</p>
            <p v-if="['News'].includes($route.name)" class="header__info"><span class="header__info--title">{{ title }}</span></p>
          </div>

          <div
            v-else
            class="header__info"
          >
            <p v-if="loading"><span class="header__info--title">Loading...</span></p>
            <p v-else-if="paginator.total === 0"><span class="header__info--title">{{ title }} not found</span></p>
            <p v-else><span class="header__info--title">{{ title }}</span> (<span>{{ $route.name === 'Search' ? 1 : separateThousands(paginator.from) }}</span> to <span>{{ paginator.to <= 10 ? separateThousands(paginator.to) : '10' }}</span> from <span>{{ separateThousands(paginator.total) }}</span> result{{ paginator.total !== 1 ? 's' : '' }})</p>
          </div>
        </div>

        <div class="header--right">
          <p v-html="viewInfo" />
        </div>
      </div>

      <div class="header--bottom">
        <LayoutControls
          :controls="accessControls"
          :loading="loading"
          @controlsChanged="controlsChanged"
        />

        <LayoutControls
          :controls="controls"
          :loading="noResults || timeout || loading ? true : false"
          @controlsChanged="controlsChanged"
        />
      </div>
    </div>

    <div
      v-else
      class="header--small"
    >
      <div class="header--left">
        <div
          v-if="type !== 'related'"
          class="header__icon"
          v-html="icon"
        />

        <div class="header__info">
          <p v-if="loading"><span class="header__info--title">Loading...</span></p>
          <p v-else-if="paginator.total === 0"><span class="header__info--title">No results</span></p>
          <p v-else-if="title === 'News'"><span class="header__info--title">{{ title }}</span></p>
          <p v-else><span :class="['header__info--title', type === 'related' ? 'header__info--lowercase' : '']">{{ title }}</span><span v-if="subtitle" :class="['header__info--subtitle', 'header__info--subtitle' + subtitleColor]">{{ subtitle }}</span><span v-if="subtitleAlt" class="header__info--title">{{ subtitleAlt }}</span> (<span>{{ separateThousands(paginator.total) }}</span><span class="header__hidden--xs">result{{ paginator.total !== 1 ? 's' : '' }}</span>)</p>
        </div>
      </div>

      <div
        v-if="!noControls && !noResults && !timeout"
        class="controls"
      >
        <LayoutControls
          :controls="controls"
          :loading="noResults || timeout || loading ? true : false"
          @controlsChanged="controlsChanged"
        />
      </div>
    </div>
  </div>
</template>

<script>
import LayoutControls from '@/components/layout/Controls.vue'
import helperMethods from '@/helpers/utility.js'

export default {
  components: {
    LayoutControls
  },
  props: {
    type: {
      type: String,
      default: 'default'
    },
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: [String, Boolean],
      default: false
    },
    subtitleAlt: {
      type: [String, Boolean],
      default: false
    },
    subtitleColor: {
      type: String,
      default: 'blue'
    },
    icon: {
      type: String,
      default: ''
    },
    paginator: {
      type: Object,
      default: function () {
        return {}
      }
    },
    accessControls: {
      type: Object,
      default: function () {
        return {}
      }
    },
    controls: {
      type: Object,
      default: function () {
        return {}
      }
    },
    noControls: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    noResults: {
      type: Boolean,
      default: false
    },
    timeout: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    settings () {
      return this.$store.getters['app/getSettings']
    },
    selectedView () {
      return this.$store.getters['search/getSelectedView']
    },
    viewInfo () {
      let view = this.settings.search.view
      let access = 'All'
      let sort = 'most relevant'

      if (this.settings[view]) {
        access = this.settings[view].access === 'locked' ? '<span class="header--rightDash">Unavailable</span>' : (this.settings[view].access === 'unlocked' ? '<span class="header--rightDash">Available</span>' : '<span class="header--rightDash">All</span>')
        sort = this.settings[view].sort === '-published' ? '<span class="header--rightDash">latest</span>' : (this.settings[view].sort === 'published' ? '<span class="header--rightDash">oldest</span>' : this.settings[view].sort === '-relevance' ? '<span class="header--rightDash">least</span> relevant' : '<span class="header--rightDash">most</span> relevant')
      }

      return access + ' results <span>/</span> ' + sort + ' first'
    }
  },
  methods: {
    controlsChanged (setting, value) {
      this.$emit('controlsChanged', setting, value)
    },
    ...helperMethods
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.header {
  @include font('content--md', $white, 0.8);
  background-color: $bg-2;
  border-top: 1px solid rgba($white, 0.1);
  border-bottom: 1px solid $black;
  width: 100%;
  user-select: none;

  @media screen and (min-width: $breakpointLg) {
    @include font('content--lg', $white, 0.8);
  }

  &--full {
    height: 74px;

    @media screen and (min-width: $breakpointLg) {
      height: 82px;
    }
  }

  &--small {
    display: flex;
    justify-content: space-between;
    height: 36px;

    @media screen and (min-width: $breakpointLg) {
      height: 40px;
    }
  }

  &--top {
    width: 100%;
    height: 36px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid $black;
    overflow: hidden;

    @media screen and (min-width: $breakpointLg) {
      height: 40px;
    }
  }

  &--bottom {
    //background-color: $bg-4;
    width: 100%;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid rgba($white, 0.1);

    @media screen and (min-width: $breakpointLg) {
      height: 40px;
    }
  }

  &--left {
    display: flex;
    align-items: center;
    flex: 1;
  }

  &--right {
    display: none;
    flex: 1;
    text-align: right;
    margin-right: 12px;

    @media screen and (min-width: $breakpointXs) {
      display: block;
    }

    /deep/ span {
      color: rgba($white, 0.5);
    }

    /deep/ &Dash {
      color: rgba($white, 0.8) !important;
      border-bottom: 1px dashed $blue;
    }
  }

  &__icon {
    width: 18px;
    min-width: 18px;
    max-width: 18px;
    height: 18px;
    margin: 0 0 0 16px;
    fill: $blue;

    @media screen and (min-width: $breakpointLg) {
      width: 20px;
      min-width: 20px;
      max-width: 20px;
      height: 20px;
    }
  }

  &__info {
    //display: flex;
    //align-items: center;
    display: -webkit-box;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;

    &--xs {
      display: -webkit-box;

      @media screen and (min-width: $breakpointSm) {
        display: none;
      }
    }

    &--sm {
      display: none;

      @media screen and (min-width: $breakpointSm) {
        display: block;
      }

      @media screen and (min-width: $breakpointMd) {
        display: none;
      }
    }

    &--md {
      display: none;

      @media screen and (min-width: $breakpointMd) {
        display: block;
      }
    }

    &--lowercase {
      text-transform: initial !important;
    }

    &--title {
      text-transform: uppercase;
      letter-spacing: 0.2px;
      margin-right: 4px;
    }

    &--subtitle {
      letter-spacing: 0.2px;
      margin-right: 4px;

      &blue {
        color: $blue !important;
      }

      &pink {
        color: $pink !important;
      }
    }

    p {
      @include font('content--md', $white, 0.5);
      margin: 0 12px;

      @media screen and (min-width: $breakpointLg) {
        @include font('content--lg', $white, 0.5);
        margin: 0 12px;
      }

      span:not(.header__hidden--xs) {
        color: rgba($white, 0.9);
      }
    }
  }

  &__hidden {
    &--xs {
      display: none;

      @media screen and (min-width: $breakpointSm) {
        display: inline-flex;
        margin-left: 4px;
      }
    }
  }
}

</style>
